import type { LexicalContent } from '@fluenceur/types';

export interface FrozenTermsBody {
  /** The heading the authoring tool gave the document, when it gave one. */
  title: string | null;
  content: LexicalContent;
}

/**
 * Reads the frozen snapshot into something the rich-text renderer accepts.
 *
 * The API stores the authoring tool's response verbatim, because the hash a
 * proof points at must designate those exact bytes. The dashboard is therefore
 * the side that unwraps it, and the side that has to survive a body it cannot
 * render: returning null lets the caller show a way to reach a human instead of
 * a blank page, at the one moment the document is what unlocks the account.
 */
export function parseFrozenTerms(raw: string | null | undefined): FrozenTermsBody | null {
  if (!raw) return null;

  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return null;
  }

  if (!parsed || typeof parsed !== 'object') return null;

  const record = parsed as Record<string, unknown>;
  const content = record.content;

  if (!content || typeof content !== 'object' || !('root' in content)) return null;

  const title = typeof record.title === 'string' && record.title.trim() ? record.title : null;

  return { title, content: content as LexicalContent };
}
